import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import type { CSSProperties, ReactNode } from "react"
import styles from "@/styles/components/Tooltip.module.css"

/*
 * A label that appears on hover, and on keyboard focus.
 *
 * It replaces `title` on the controls that are a glyph and nothing else — the
 * round rail, the phase chips. `title` was never good enough for those: it waits
 * about a second, it is styled by the operating system rather than by this
 * system, and it never appears for someone arriving by keyboard, which is
 * exactly the person who cannot see what the icon is.
 *
 * PORTALLED, and positioned `fixed` off the trigger's own rect. The rail is an
 * `overflow-x: auto` capsule, so a bubble rendered inside it would be clipped by
 * the very container it has to escape; a portal to `document.body` is the only
 * shape that does not force the rail to give up its scrolling.
 *
 * Deliberately `aria-hidden`. Assistive technology is served by the trigger's own
 * `aria-label`, which every caller sets to the SAME string passed here — a
 * tooltip that also announced itself would read the control's name out twice.
 * So: if the label says why a round is locked, the `aria-label` has to say it
 * too.
 */

/** Long enough that sweeping the pointer across the rail does not flash six of
 *  them, short enough that it does not feel like `title` again. */
const OPEN_DELAY_MS = 250

/** How close the bubble may come to the viewport edge before it is pushed back
 *  in. Matches the gutter the fixed bars keep. */
const EDGE_GUTTER = 12

/*
 * A trigger sitting in a bar can name that bar as the edge the bubble squares
 * up to: `data-tooltip-boundary` on the container, and the bubble stops flush
 * with it rather than centring off the trigger and hanging over the side. The
 * vote round's "Locked In" is the case it exists for — the bubble is wider than
 * the button, so centred it spills past the capsule it belongs to.
 */
const BOUNDARY = "[data-tooltip-boundary]"

/** Breathing room kept inside the boundary, so the bubble squares up to the bar
 *  without touching its edge. */
const BOUNDARY_INSET = 18

interface TooltipProps {
  /** The text of the bubble, and what the trigger's own `aria-label` should say. */
  label: ReactNode
  children: ReactNode
  /** Which side of the trigger it sits on. */
  placement?: "top" | "bottom"
  /**
   * Hover delay before it opens. Defaults to `OPEN_DELAY_MS`, which is tuned so
   * sweeping across the rail's many chips does not flash a row of them. A control
   * standing on its own — a single bar icon — can pass a shorter delay so it does
   * not feel like `title` again.
   */
  delay?: number
  className?: string
}

interface Anchor {
  x: number
  y: number
  /** The rect the bubble must stay inside, if the trigger named one. */
  bounds: { left: number; right: number } | null
}

export default function Tooltip({
  label,
  children,
  placement = "bottom",
  delay = OPEN_DELAY_MS,
  className,
}: TooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null)
  const bubbleRef = useRef<HTMLSpanElement>(null)
  const timer = useRef<number | null>(null)
  const [anchor, setAnchor] = useState<Anchor | null>(null)
  /*
   * How far the bubble had to slide off centre to stay on screen. A trigger at
   * the end of a bottom bar — the vote round's "Locked In" — is centred close
   * enough to the edge that a 340px bubble hangs off it, so centring is a
   * preference here, not a rule.
   */
  const [shift, setShift] = useState(0)

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }

  const hide = useCallback(() => {
    clearTimer()
    setAnchor(null)
  }, [])

  /*
   * Measured at the moment it opens rather than held in state, because the rail
   * scrolls sideways and the page scrolls under it: a position captured on mount
   * would put the bubble where the pill used to be.
   */
  const show = useCallback(() => {
    clearTimer()
    timer.current = window.setTimeout(() => {
      const trigger = triggerRef.current
      const rect = trigger?.getBoundingClientRect()
      if (!trigger || !rect) return
      const boundary = trigger.closest(BOUNDARY)?.getBoundingClientRect()
      setAnchor({
        x: rect.left + rect.width / 2,
        y: placement === "top" ? rect.top : rect.bottom,
        bounds: boundary ? { left: boundary.left, right: boundary.right } : null,
      })
    }, delay)
  }, [placement, delay])

  useLayoutEffect(() => {
    if (!anchor) return
    const width = bubbleRef.current?.getBoundingClientRect().width
    if (!width) return
    const half = width / 2

    // The named boundary first, flush to its edges; the viewport is the fallback
    // and also the backstop for a bubble too wide for the boundary to hold.
    let min = EDGE_GUTTER + half
    let max = window.innerWidth - EDGE_GUTTER - half
    if (anchor.bounds && anchor.bounds.right - anchor.bounds.left >= width + BOUNDARY_INSET * 2) {
      min = Math.max(min, anchor.bounds.left + BOUNDARY_INSET + half)
      max = Math.min(max, anchor.bounds.right - BOUNDARY_INSET - half)
    }

    // Narrower viewport than bubble: leave it centred rather than fight itself.
    setShift(min > max ? 0 : Math.min(Math.max(anchor.x, min), max) - anchor.x)
  }, [anchor])

  // Nothing repositions an open bubble: it is dismissed instead. Following the
  // trigger through a scroll would mean a listener per tooltip on the page.
  useEffect(() => {
    if (!anchor) return
    const dismiss = () => setAnchor(null)
    window.addEventListener("scroll", dismiss, true)
    window.addEventListener("resize", dismiss)
    return () => {
      window.removeEventListener("scroll", dismiss, true)
      window.removeEventListener("resize", dismiss)
    }
  }, [anchor])

  useEffect(() => clearTimer, [])

  return (
    <>
      <span
        ref={triggerRef}
        className={className ? `${styles.trigger} ${className}` : styles.trigger}
        onPointerEnter={(event) => {
          // Touch already fires the control itself; a bubble on top of the tap
          // would cover the thing that just responded to it.
          if (event.pointerType === "touch") return
          show()
        }}
        onPointerLeave={hide}
        onPointerDown={hide}
        /* Focus and blur are listened for on the wrapper, which catches them as
         * they bubble from whatever the caller put inside — the trigger is not
         * always a single element, and it is never this span. */
        onFocus={(event) => {
          const target = event.target
          if (target instanceof HTMLElement && !target.matches(":focus-visible")) return
          show()
        }}
        onBlur={hide}
        onKeyDown={(event) => {
          if (event.key === "Escape") hide()
        }}
      >
        {children}
      </span>

      {anchor
        ? createPortal(
            <span
              role="presentation"
              aria-hidden="true"
              ref={bubbleRef}
              className={placement === "top" ? `${styles.bubble} ${styles.top}` : styles.bubble}
              /* The arrow stays over the trigger while the bubble slides off
               * centre, so `--arrow-shift` undoes the clamp for it alone. */
              style={
                {
                  left: `${anchor.x + shift}px`,
                  top: `${anchor.y}px`,
                  "--arrow-shift": `${shift}px`,
                } as CSSProperties
              }
            >
              {label}
            </span>,
            document.body
          )
        : null}
    </>
  )
}
